import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { UseMutationResult, UseQueryResult } from "@tanstack/react-query";
import { cloneRole, createRole, deleteRole, getRoles, renameRole } from "../api/rbac-api";
import type {
	CloneRolePayload,
	CreateRolePayload,
	RenameRolePayload,
	RoleResource,
} from "../types/RolesPermissions.type";

export const ROLES_KEYS = {
	all: ["rbac", "roles"] as const,
	list: () => [...ROLES_KEYS.all, "list"] as const,
};

export const useRoles = (): UseQueryResult<RoleResource[]> => {
	return useQuery({
		queryKey: ROLES_KEYS.list(),
		queryFn: getRoles,
	});
};

export const useCloneRole = (): UseMutationResult<
	RoleResource,
	unknown,
	CloneRolePayload
> => {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: (payload) => cloneRole(payload),
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: ROLES_KEYS.list() });
		},
	});
};

export const useCreateRole = (): UseMutationResult<
	RoleResource,
	unknown,
	CreateRolePayload
> => {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: (payload) => createRole(payload),
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: ROLES_KEYS.list() });
		},
	});
};

export const useRenameRole = (): UseMutationResult<
	RoleResource,
	unknown,
	{ roleId: number; payload: RenameRolePayload }
> => {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: ({ roleId, payload }) => renameRole(roleId, payload),
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: ROLES_KEYS.list() });
		},
	});
};

export const useDeleteRole = (): UseMutationResult<void, unknown, { roleId: number }> => {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: ({ roleId }) => deleteRole(roleId),
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: ROLES_KEYS.list() });
		},
	});
};
